import Table, {
  buildActionColumn,
  buildAvatarDetailsColumn,
  buildCopyTextColumn,
  buildExpandableCountColumn,
  TableControls
} from "@/components/Table";
import { CellContext, ColumnDef } from "@tanstack/react-table";
import IconButton from "@/components/IconButton";
import useTableRowMenu, { actionId } from "@/hooks/useTableRowMenu";
import type { Action } from "@/components/MenuButton";
import SearchInput from "@/components/SearchInput";
import { useIsCompactLayout } from "@/hooks/useIsCompactLayout";
import { useQuery, useMutation, useApolloClient } from "@apollo/client";
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { SOME_PEOPLE_QUERY } from "@/pages/people/queries";
import EmptyState from "@/components/EmptyState";
import { JobPost, JobApplicationWithReviews, Review } from "../types";
import ApplicantDetailsDialog from "./ApplicantDetailsDialog";
import { useRequireCapability } from "@/hooks/useCapabilities";
import { DOMAIN, PERMISSION } from "@/providers/AuthorizationProvider";
import { DELETE_JOB_APPLICATION_REVIEW, JOB_POST_WITH_APPLICATION_REVIEWS_QUERY, updateCache } from "../queries";
import { showErrorSnackbar, showSnackbar } from "@/lib/snackbarUtils";
import { buildExportReviewsAction } from "./ApplicantReviewsExportButton";
import TableDetailsPanel from "@/components/TableDetailsPanel";
import CreateApplicationReviewDialog from "./CreateApplicationReviewDialog";
import Button from "@/components/Button";
import Dialog from "@/components/Dialog";
import Tooltip from "@/components/Tooltip";
import Icon from "@/components/Icon";
import useCurrentUser from "@/hooks/useCurrentUser";
import { BackofficePerson } from "@/types";
import ApplicantReviewSlackRequestDialog from "./ApplicantReviewSlackRequestDialog";
import { ColumnSort } from "@tanstack/react-table";
import useCapabilities from "@/hooks/useCapabilities";
import classNames from "classnames";
import MetricGrid from "@/components/MetricGrid";
import { MetricCardType } from "@/components/MetricCard/MetricCard.types";
import { Path } from "@/router";
import UpdateJobApplicationFilesModal from "@/components/UpdateJobApplicationFilesModal";
import {
  canExpandApplicationReviews,
  filterJobApplicationsBySearch,
  formatApplicationReviewsSummary
} from "./jobApplicationsTableUtils";
import useAmplitude from "@/hooks/useAmplitude";
import {
  JOBS_ANALYTICS_EVENTS,
  getJobsAnalyticsData,
  getJobsApplicationsListFilterAnalyticsData
} from "@/pages/jobs/_utils/amplitude";

const RATING_OPTIONS = [
  { value: "1", label: "Decline" },
  { value: "2", label: "Consider" },
  { value: "3", label: "Advance" }
];

interface ViewJobPostReviewsProps {
  jobPost: JobPost;
}

export default function ViewJobPostReviews({ jobPost }: ViewJobPostReviewsProps) {
  const { Menu, setMenu, reposition } = useTableRowMenu();
  const { isCompactLayout } = useIsCompactLayout();
  const { hasPermission } = useCapabilities();
  const { trackEvent } = useAmplitude();
  const canManageReviews = hasPermission(PERMISSION.manageJobPosts);
  const [selectedApplicant, setSelectedApplicant] = useState<JobApplicationWithReviews | null>(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [metrics, setMetrics] = useState<MetricCardType<Path>[]>([]);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [applications, setApplications] = useState<JobApplicationWithReviews[]>([]);
  const [orderBy, setOrderBy] = useState<ColumnSort[]>([{ id: "createdAt", desc: true }]);
  const [searchValue, setSearchValue] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const previousSearchRef = useRef(debouncedSearch);
  const [updateModalOpen, setUpdateModalOpen] = useState(false);
  const [selectedApplicationId, setSelectedApplicationId] = useState<string | null>(null);
  const [deletingApplicationId, setDeletingApplicationId] = useState<string | null>(null);

  const { data, loading, refetch } = useQuery<{ jobPost: JobPost }>(JOB_POST_WITH_APPLICATION_REVIEWS_QUERY, {
    variables: { jobPostId: jobPost?.id },
    skip: !jobPost?.id,
    fetchPolicy: "cache-first"
  });
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [application, setApplication] = useState<JobApplicationWithReviews | null>(null);
  const [reviewToDelete, setReviewToDelete] = useState<Review | null>(null);
  const [deleteType, setDeleteType] = useState<"review" | "recommendation" | null>(null);
  const [isRecommendation, setIsRecommendation] = useState(false);
  const [reviewToEdit, setReviewToEdit] = useState<Review | null>(null);

  // Request reviews.
  const [requestModalOpen, setRequestModalOpen] = useState(false);
  const [requestVariables, setRequestVariables] = useState<{
    reviewers: string[];
    reviewerIds: string[];
    message: string;
  }>({
    reviewers: [],
    reviewerIds: [],
    message: ""
  });

  const { cache } = useApolloClient();
  const { user } = useCurrentUser();

  useEffect(() => {
    if (data) {
      setApplications(data.jobPost.applications || []);
      updateCache(cache, data.jobPost.id, "applications", data.jobPost.applications);
    } else {
      setApplications([]);
    }
  }, [cache, data]);

  useRequireCapability({
    domain: DOMAIN.JOBS,
    permission: PERMISSION.manageJobPosts
  });

  const handleAddReview = (app: any, isRecommendation: boolean) => {
    const existingRecommendation = isRecommendation ? app.reviews?.find((r: any) => r.isRecommendation) : null;

    setApplication(app);
    setIsRecommendation(isRecommendation);
    setIsDialogOpen(true);

    if (existingRecommendation) {
      // Sort reviews by createdAt to get the latest one
      const sortedReviews = [...app.reviews].sort(
        (a: any, b: any) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );
      const latestRecommendation = sortedReviews.find((r: any) => r.isRecommendation);
      setReviewToEdit(latestRecommendation);
    } else {
      setReviewToEdit(null);
    }
  };

  useQuery(SOME_PEOPLE_QUERY, {
    variables: {
      input: {
        search: `${selectedApplicant?.applicant?.firstName} ${selectedApplicant?.applicant?.lastName}`
      },
      first: 1
    },
    skip: !selectedApplicant,
    onCompleted: (data) => {
      if (data?.people?.nodes[0]) {
        setSelectedApplicant((prev: any) => ({
          ...prev!,
          applicant: {
            ...prev!.applicant,
            ...data.people.nodes[0]
          }
        }));
      }
    }
  });

  const handleViewDetails = (application: any) => {
    setSelectedApplicant(application);
    setIsSidebarOpen(true);
  };

  const handleUpdateFiles = (application: JobApplicationWithReviews) => {
    if (!application.id) {
      showErrorSnackbar("Application files not updated", { subtitle: "Application ID is missing." });
      return;
    }
    setSelectedApplicationId(application.id);
    setUpdateModalOpen(true);
  };

  const handleCloseSidebar = () => {
    setIsSidebarOpen(false);
    setSelectedApplicant(null);
  };

  useEffect(() => {
    // Calculate total reviews
    const totalReviews = applications.reduce((total, app) => {
      return total + (app.reviews?.filter((review) => !review.isRecommendation).length || 0);
    }, 0);

    // Calculate unique reviewers
    const uniqueReviewers = new Set(
      applications.flatMap(
        (app) => app.reviews?.map((review: { reviewedBy?: { name: string } }) => review.reviewedBy?.name) || []
      )
    ).size;

    setMetrics([
      {
        label: "Total Reviews",
        value: `${totalReviews}`,
        iconName: "rate_review"
      },
      {
        label: "Unique Reviewers",
        value: `${uniqueReviewers}`,
        iconName: "rate_review"
      }
    ]);
  }, [applications]);

  const refetchApplications = () => {
    refetch();
  };

  const handleDelete = (application: JobApplicationWithReviews) => {
    const recommendation = application.reviews?.find((r) => r.isRecommendation);
    if (!recommendation) return;

    setReviewToDelete(recommendation);
    setDeletingApplicationId(application.id);
    setDeleteType("recommendation");
    setIsDeleteModalOpen(true);
  };

  const [deleteReview, { loading: isDeletingReview }] = useMutation(DELETE_JOB_APPLICATION_REVIEW, {
    onCompleted: (data) => {
      const result = data?.deleteJobApplicationReview;
      if (!result?.success) {
        const message = result?.message;
        showErrorSnackbar("Review not deleted", {
          subtitle: message || "Please try again later."
        });
        return;
      }

      trackEvent(
        JOBS_ANALYTICS_EVENTS.applicationReviewDeleted,
        getJobsAnalyticsData({
          source: "jobs_application_review_delete",
          jobPostId: jobPost.id,
          jobApplicationId: deletingApplicationId ?? undefined,
          action: "delete_review",
          isRecommendation: deleteType === "recommendation"
        })
      );
      setReviewToDelete(null);
      setDeletingApplicationId(null);
      setIsDeleteModalOpen(false);
      refetchApplications();
      showSnackbar("Review deleted", { close: true });
    },
    onError: () => {
      showErrorSnackbar("Review not deleted", { subtitle: "Please try again later." });
    }
  });

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchValue);
    }, 300);
    return () => clearTimeout(handler);
  }, [searchValue]);

  const trackFilterChange = useCallback(
    (filter: string, value: boolean) => {
      trackEvent(
        JOBS_ANALYTICS_EVENTS.applicationsListFiltered,
        getJobsApplicationsListFilterAnalyticsData({
          source: "jobs_application_reviews",
          filter,
          value
        })
      );
    },
    [trackEvent]
  );

  useEffect(() => {
    const previousSearch = previousSearchRef.current.trim();
    const currentSearch = debouncedSearch.trim();
    if (currentSearch !== previousSearch) {
      trackFilterChange("search", Boolean(currentSearch));
      previousSearchRef.current = debouncedSearch;
    }
  }, [debouncedSearch, trackFilterChange]);

  const handleDeleteConfirm = () => {
    if (!reviewToDelete?.id) {
      showErrorSnackbar("Review not deleted", { subtitle: "Review ID is missing." });
      return;
    }
    deleteReview({ variables: { id: reviewToDelete.id } });
  };

  const handleSort = (updaterOrValue: any) => {
    const sortState = typeof updaterOrValue === "function" ? updaterOrValue([]) : updaterOrValue;
    setOrderBy(sortState);
  };

  const filteredApplications = useMemo(
    () => filterJobApplicationsBySearch(applications, searchValue),
    [applications, searchValue]
  );

  const pageActions = useMemo((): Action[] => {
    return [
      buildExportReviewsAction({
        applications,
        jobTitle: jobPost?.name ?? "Job Position",
        facilityName: jobPost?.facility?.name ?? "Unknown Facility",
        disabled: loading && !data
      }),
      {
        text: "Request Reviews",
        variant: "text",
        iconName: "ios_share",
        testId: "request-reviews-button",
        onClick: () => setRequestModalOpen(true)
      }
    ];
  }, [applications, data, jobPost?.facility?.name, jobPost?.name, loading]);

  const getReviewActions = (original: JobApplicationWithReviews): Action[] => {
    const hasRecommendation = original.reviews?.some((r) => r.isRecommendation);

    const actions: Action[] = [
      {
        text: hasRecommendation ? "Edit Recommendation" : "Add Recommendation",
        section: "Manage",
        onClick: () => handleAddReview(original, true)
      },
      {
        text: "Add Review",
        section: "Manage",
        onClick: () => handleAddReview(original, false)
      },
      {
        text: "Update Files",
        section: "Manage",
        onClick: () => handleUpdateFiles(original)
      }
    ];

    if (hasRecommendation && canManageReviews) {
      actions.push({
        text: "Delete Recommendation",
        iconName: "delete",
        destructive: true,
        onClick: () => handleDelete(original)
      });
    }

    return actions;
  };

  const tableColumns: ColumnDef<JobApplicationWithReviews>[] = [
    ...buildAvatarDetailsColumn<JobApplicationWithReviews>({
      isCompactLayout,
      getTitle: (row) => `${row.applicant.firstName} ${row.applicant.lastName}`,
      getSubtitle: (row) => row.applicant?.latestProgram?.programType?.name || "",
      subtitleLabel: "Program",
      getPicture: (row) => row.applicant?.profilePhoto,
      header: "Applicant Name",
      size: 300,
      id: "name",
      sortType: "text"
    }),
    buildCopyTextColumn<JobApplicationWithReviews>({
      accessorKey: "applicant.pgaId",
      header: "PGA ID",
      copyFieldLabel: "PGA ID",
      getText: (row) => row.applicant?.pgaId || "",
      shouldShowCopyButton: (row) => !!row.applicant?.pgaId,
      enableSorting: false
    }),
    {
      id: "section",
      header: "Section",
      accessorFn: (row) => row.applicant?.primarySection?.name || "",
      cell: ({ row }) => row.original.applicant.primarySection?.name || <span className="opacity-20">--</span>
    },
    {
      id: "resume",
      header: "Resume",
      accessorKey: "resume",
      cell: ({ row }) =>
        row.original.resume ? (
          <a
            href={row.original.resume}
            className="text-sys-brand-primary hover:underline"
            onClick={(e) => e.stopPropagation()}
            target="_blank"
            rel="noreferrer"
          >
            Resume
          </a>
        ) : (
          <span className="opacity-20">--</span>
        )
    },
    {
      id: "coverLetter",
      header: "Cover Letter",
      accessorKey: "coverLetter",
      cell: ({ row }) =>
        row.original.coverLetter ? (
          <a
            href={row.original.coverLetter}
            className="text-label-large text-sys-brand-primary hover:underline"
            onClick={(e) => e.stopPropagation()}
            target="_blank"
            rel="noreferrer"
          >
            Cover Letter
          </a>
        ) : (
          <span className="opacity-20">--</span>
        )
    },
    buildExpandableCountColumn<JobApplicationWithReviews>({
      id: "reviews",
      accessorKey: "reviewScoreAverage",
      header: "Total Reviews / Average Rating",
      enableSorting: true,
      sortingFn: "basic",
      getCount: (row) => row.reviews?.filter((review) => !review.isRecommendation).length || 0,
      getHasItems: (row) => canExpandApplicationReviews(row),
      getDisplayText: (row, count) => formatApplicationReviewsSummary(row, count)
    }),
    {
      id: "recommendation",
      header: "Recommendation",
      accessorKey: "recommendation",
      cell: ({ row }) => {
        const reviews = row.original.reviews || [];
        const recommendation = reviews.find((r) => r.isRecommendation);

        if (!recommendation) return <div className="flex items-center opacity-20">--</div>;

        return (
          <div className="flex items-center">
            <div className="flex items-center absolute">
              <div className="flex items-center">
                <Tooltip
                  title={<div className="text-title-small text-sys-brand-on-surface-variant">Recommendation</div>}
                  supportingText={
                    <div className="text-body-medium text-sys-brand-on-surface-variant">
                      <div className="mb-2">{recommendation.comments}</div>
                      <br />
                      <div>
                        <div>
                          <span className="font-semibold">Reviewer:</span> {recommendation.reviewedBy?.name || "--"}
                        </div>
                        <div>
                          <span className="font-semibold">Review Date:</span>{" "}
                          {recommendation.createdAt ? new Date(recommendation.createdAt).toLocaleDateString() : "--"}
                        </div>
                      </div>
                    </div>
                  }
                  menuCorner="end-start"
                  anchorCorner="start-start"
                >
                  <div className="flex items-center gap-2">
                    <div className="text-title-small text-sys-brand-on-surface-variant">
                      {RATING_OPTIONS.find((option) => option.value === String(recommendation.score))?.label}
                    </div>
                    <Icon name="sticky_note_2" className="text-sys-brand-on-surface" />
                  </div>
                </Tooltip>
              </div>
            </div>
          </div>
        );
      }
    },
    buildActionColumn<JobApplicationWithReviews>({
      actionId,
      setMenu,
      getRowId: (row) => row.id,
      getActions: getReviewActions,
      onViewClick: (row) => handleViewDetails(row),
      viewButtonTitle: "View applicant details"
    })
  ];

  const hasActiveSearch = !!searchValue.trim();

  return (
    <>
      <Menu />
      {!!metrics.length && <MetricGrid metrics={metrics} layout="expanded" />}
      <div className="pt-12" />
      <Table
        data={filteredApplications}
        columns={tableColumns}
        loading={loading && !data}
        onSortingChange={handleSort}
        sortingState={orderBy}
        serverSideSorting={false}
        onHorizontalScroll={reposition}
        hasActiveFilters={hasActiveSearch}
        getRowCanExpand={(row) => canExpandApplicationReviews(row.original)}
        renderControls={(_table, { onSortClick } = {}) => (
          <TableControls actions={pageActions} onSortClick={onSortClick}>
            <SearchInput
              placeholder="Search Applicants"
              query={searchValue}
              onChange={setSearchValue}
              onClear={() => setSearchValue("")}
            />
          </TableControls>
        )}
        renderFilteredEmptyState={() => (
          <EmptyState title="No Results Found" caption="Try a different search." iconName="search" />
        )}
        renderNoDataEmptyState={() => (
          <EmptyState
            title="No Applicant Reviews"
            caption="There are no applicant reviews associated with this job."
            iconName="person"
          />
        )}
        onClickRow={(row) => {
          handleViewDetails(row.original);
        }}
        renderExpandedRow={({ row }) => {
          const regularReviews = (row.original.reviews || []).filter((review) => !review.isRecommendation);
          return (
            <div className="bg-sys-surface-container-lowest rounded-b-xl p-2">
              <TableDetailsPanel
                title={`Reviews (${regularReviews.length})`}
                data={regularReviews}
                columns={[
                  {
                    accessorKey: "reviewedBy.name",
                    header: "Reviewer",
                    cell: (cellProps: CellContext<any, any>) => cellProps.row.original.reviewedBy?.name || "--"
                  },
                  {
                    accessorKey: "createdAt",
                    header: "Review Date",
                    cell: (cellProps: CellContext<any, any>) =>
                      cellProps.row.original.createdAt
                        ? new Date(cellProps.row.original.createdAt).toLocaleDateString()
                        : "--"
                  },
                  {
                    accessorKey: "score",
                    header: "Rating"
                  },
                  {
                    accessorKey: "comments",
                    header: "Review Comments"
                  },
                  {
                    id: "delete",
                    header: "",
                    cell: (cellProps: CellContext<any, any>) => {
                      const isReviewer = cellProps.row.original.reviewedBy?.name === user.name;
                      if (!isReviewer) return null;

                      return (
                        <div className="flex items-center justify-end">
                          <IconButton
                            name="delete"
                            className="text-error"
                            aria-label="Delete Review"
                            onClick={() => {
                              setReviewToDelete(cellProps.row.original);
                              setDeletingApplicationId(row.original.id);
                              setDeleteType(cellProps.row.original.isRecommendation ? "recommendation" : "review");
                            }}
                          />
                        </div>
                      );
                    }
                  }
                ]}
              />
            </div>
          );
        }}
      />
      <ApplicantDetailsDialog
        applicant={
          selectedApplicant
            ? ({
                ...selectedApplicant.applicant,
                employments: [],
                phoneNumbers: [],
                addresses: [],
                contactAddressId: null,
                membershipSince: null,
                primarySectionAffiliation: selectedApplicant.applicant.primarySection
                  ? {
                      section: selectedApplicant.applicant.primarySection
                    }
                  : undefined,
                primaryFacility: undefined
              } as unknown as BackofficePerson)
            : null
        }
        applicationEmail={selectedApplicant?.email}
        applicationPhone={selectedApplicant?.phone}
        open={isSidebarOpen}
        onClose={handleCloseSidebar}
      />
      <CreateApplicationReviewDialog
        open={isDialogOpen}
        onClose={() => {
          setIsDialogOpen(false);
          setReviewToEdit(null);
        }}
        jobApplicationId={application?.id || ""}
        applicantName={`${application?.applicant.firstName} ${application?.applicant.lastName}`}
        isRecommendation={isRecommendation}
        onSuccess={refetchApplications}
        reviewToEdit={reviewToEdit}
      />
      <Dialog
        open={isDeleteModalOpen}
        onClosed={() => {
          setIsDeleteModalOpen(false);
          setDeletingApplicationId(null);
        }}
        headline="Delete Recommendation"
        className="w-[490px] p-6 pr-2"
        actions={
          <div className="flex gap-2">
            <Button
              variant="text"
              onClick={() => {
                setIsDeleteModalOpen(false);
                setDeletingApplicationId(null);
              }}
              disabled={isDeletingReview}
            >
              Cancel
            </Button>
            <Button
              variant="filled"
              theme="error"
              trailingIcon={false}
              icon="delete"
              onClick={handleDeleteConfirm}
              disabled={isDeletingReview}
              data-testid="confirm-delete-recommendation"
            >
              Delete
            </Button>
          </div>
        }
      >
        <p>Are you sure you want to delete this recommendation? This cannot be undone.</p>
      </Dialog>
      <Dialog
        open={!!reviewToDelete}
        onClosed={() => {
          setReviewToDelete(null);
          setDeletingApplicationId(null);
        }}
        headline={deleteType === "recommendation" ? "Delete Recommendation" : "Delete Review"}
        className="w-[490px] p-6 pr-2"
        actions={
          <div className="flex gap-2">
            <Button
              variant="text"
              onClick={() => {
                setReviewToDelete(null);
                setDeletingApplicationId(null);
              }}
              disabled={isDeletingReview}
            >
              Cancel
            </Button>
            <Button
              variant="filled"
              theme="error"
              trailingIcon={false}
              iconClassName={classNames(isDeletingReview && "animate-spin w-6 h-6 !block")}
              icon={isDeletingReview ? "progress_activity" : "delete"}
              onClick={handleDeleteConfirm}
              disabled={isDeletingReview || !reviewToDelete?.id}
              data-testid="confirm-delete-review"
            >
              Delete
            </Button>
          </div>
        }
      >
        <p>Are you sure you want to delete this {deleteType}? This cannot be undone.</p>
      </Dialog>

      <ApplicantReviewSlackRequestDialog
        open={requestModalOpen}
        setOpen={setRequestModalOpen}
        requestVariables={requestVariables}
        setRequestVariables={setRequestVariables}
        disabled={false}
      />
      {updateModalOpen && selectedApplicationId && (
        <UpdateJobApplicationFilesModal
          isOpen={updateModalOpen}
          onClose={() => {
            setUpdateModalOpen(false);
            setSelectedApplicationId(null);
          }}
          applicationId={selectedApplicationId}
          onSuccess={() => {
            setUpdateModalOpen(false);
            setSelectedApplicationId(null);
            refetchApplications();
          }}
        />
      )}
    </>
  );
}
